import * as React from 'react';
import Rating from 'react-rating';
import classNames from 'classnames';
import styles from './rate.scss';
import { IRateProperty } from './rate.d';
import XIcon from '../../../../Assets/svg/x';
import { SortContext } from '../../../globalState/sortContextProvider';
import { ISortContext } from '../../../globalState/sortContextProvider.d';
import TabNavigationHelper from '../../../../Common/tabNavigationHelper';
import { emulateTab } from 'emulate-tab';
import * as helper from '../../../../Common/html-helper';

export default class Rate extends React.Component<IRateProperty> {
    static contextType = SortContext;

    declare context: React.ContextType<typeof SortContext>;
    private tabNav: TabNavigationHelper;
    private emptySymbols: JSX.Element[];
    private fullSymbols: JSX.Element[];
    private readonly rpcRef = React.createRef<HTMLDivElement>();
    private readonly ratingRef = React.createRef<Rating>();
    private itWasClearSelectedRateBtn = false;

    constructor(props, context) {
        super(props, context);
        this.tabNav = TabNavigationHelper.instance;

        this.clearSelectedRate = this.clearSelectedRate.bind(this);
        this.onRateChange = this.onRateChange.bind(this);
        this.handleRpcKeyDown = this.handleRpcKeyDown.bind(this);

        this.init();
    }

    componentDidMount(): void {
        if (this.rpcRef.current) {
            this.tabNav.setFocusAtt(this.rpcRef.current);

            this.rpcRef.current.querySelectorAll('.fas_rate_point_hover').forEach((el, key) => {
                if (key + 1 <= this.props.glovesFilterSort.glovesRate) {
                    this.tabNav.setFocusAtt(el);
                } else {
                    this.tabNav.removeFocusAtt(el);
                }
            });
        }
    }

    private handleRpcKeyDown(event): void {
        if(this.itWasClearSelectedRateBtn) {
            event.preventDefault();
            event.stopPropagation();
            this.itWasClearSelectedRateBtn = false;

            return;
        }

        if (event.key === ' ') { // space btn
            event.preventDefault();
            event.stopPropagation();
    
            if (this.rpcRef.current && this.ratingRef.current) {
                const tmp = parseInt(event.target.dataset.ratepoint, 10);
                
                this.ratingRef.current.setState({
                    value: tmp
                }, () => {
                    this.onRateChange(tmp);
                });

                this.rpcRef.current.querySelectorAll('.fas_rate_point_hover').forEach((el, key) => {
                    if (key + 1 <= tmp) {
                        this.tabNav.setFocusAtt(el);
                    } else {
                        this.tabNav.removeFocusAtt(el);
                    }
                });

                // focus entire row: after click on rate point: 
                this.tabNav.focusToElement(null, this.rpcRef.current);
            }
        }

        if (event.key === 'Escape') {
            event.preventDefault();
            this.tabNav.removeGroupAtt(this.rpcRef.current);
            this.tabNav.setFocusAtt(this.rpcRef.current);
        }

        if(event.key === 'ArrowRight') {
            emulateTab();
        }

        if(event.key === 'ArrowLeft') {
            emulateTab.backwards();
        }

        if(event.key === 'ArrowDown') {
            event.preventDefault();
            const rpcIndex = parseInt(this.rpcRef.current.dataset.rpcindex, 10);
            const siblingIndex = rpcIndex === 12 ? 12 : rpcIndex + 1;

            const nextSibling = document.querySelector('[data-rpcindex="' + siblingIndex + '"]');
            window.shell.tabNav.focus(nextSibling, 'keyboard');
        }

        if(event.key === 'ArrowUp') {
            event.preventDefault();
            const rpcIndex = parseInt(this.rpcRef.current.dataset.rpcindex, 10);
            const siblingIndex = rpcIndex === 1 ? 1 : rpcIndex - 1;

            const nextSibling = document.querySelector('[data-rpcindex="' + siblingIndex + '"]');
            window.shell.tabNav.focus(nextSibling, 'keyboard');
        }
    }

    public render() {
        return (
            <div className={styles.rating} ref={(el) => this.tabNav.setGroupAtt(el)}>
                <div className={styles.rate_title}>
                    <span className={styles.title_text}>
                        {helper.ssrSafeDecodeHTML(this.props.glovesFilterSort.title)}
                    </span>
                </div>
                <div className={styles.rp_container} ref={this.rpcRef} data-rpcindex={this.props.rpcIndex + 1}
                    onKeyDown={this.handleRpcKeyDown}>
                    {this.renderClearSelectedRatingBtn()}
                    <Rating className={styles.rating_points}
                        ref={this.ratingRef}
                        onChange={this.onRateChange}
                        initialRating={this.props.glovesFilterSort.glovesRate}
                        emptySymbol={this.emptySymbols}
                        fullSymbol={this.fullSymbols}
                        readonly={this.props.mobileTouchStarted} />
                </div>
            </div>
        );
    }

    private renderClearSelectedRatingBtn(): JSX.Element {
        return (
            this.props.glovesFilterSort.glovesRate > 0 ? <span className={styles.clear_selected_rating_btn}
                ref={(el)=>this.tabNav.setFocusAtt(el)}
                data-testid='clear_selected_rating_btn'
                data-semantic-id='clear_selected_rating_btn'
                onClick={this.clearSelectedRate}><XIcon /></span> :
                <span className={styles.btn_place_holder}></span>
        );
    }

    private init(): void {
        this.emptySymbols = [];
        this.fullSymbols = [];

        this.emptySymbols.push(<div className={styles.rate_point}>
            <div className={classNames(styles.rp_shape_1, 'fas_empty_r_point')} 
            data-ratepoint='1'
            ref={(el)=>this.tabNav.setFocusAtt(el)} />
        </div>);
        this.emptySymbols.push(<div className={styles.rate_point}>
            <div className={classNames(styles.rp_shape_2, 'fas_empty_r_point')}
            data-ratepoint='2'
            ref={(el)=>this.tabNav.setFocusAtt(el)} />
        </div>);
        this.emptySymbols.push(<div className={styles.rate_point}>
            <div className={classNames(styles.rp_shape_3, 'fas_empty_r_point')}
            data-ratepoint='3'
            ref={(el)=>this.tabNav.setFocusAtt(el)} />
        </div>);
        this.emptySymbols.push(<div className={styles.rate_point}>
            <div className={classNames(styles.rp_shape_4, 'fas_empty_r_point')}
            data-ratepoint='4'
            ref={(el)=>this.tabNav.setFocusAtt(el)} />
        </div>);
        this.emptySymbols.push(<div className={styles.rate_point}>
            <div className={classNames(styles.rp_shape_5, 'fas_empty_r_point')}
            data-ratepoint='5'
            ref={(el)=>this.tabNav.setFocusAtt(el)} />
        </div>);

        this.fullSymbols.push(<div className={classNames(styles.rate_point, styles.rate_point_hover)}
            data-ratepoint='1'>
            <div className={styles.rp_shape_1} />
        </div>);
        this.fullSymbols.push(<div className={classNames(styles.rate_point, styles.rate_point_hover)}
            data-ratepoint='2'>
            <div className={styles.rp_shape_2} />
        </div>);
        this.fullSymbols.push(<div className={classNames(styles.rate_point, styles.rate_point_hover)}
            data-ratepoint='3'>
            <div className={styles.rp_shape_3} />
        </div>);
        this.fullSymbols.push(<div className={classNames(styles.rate_point, styles.rate_point_hover)}
            data-ratepoint='4'>
            <div className={styles.rp_shape_4} />
        </div>);
        this.fullSymbols.push(<div className={classNames(styles.rate_point, styles.rate_point_hover)}
            data-ratepoint='5'>
            <div className={styles.rp_shape_5} />
        </div>);
    }

    private clearSelectedRate(): void {
        this.itWasClearSelectedRateBtn = true;
        const sortContext: ISortContext = this.context;
        sortContext.setGloveRating(this.props.glovesFilterSort, 0, !this.props.isMobile,
            !this.props.isMobile, !this.props.isMobile);
    }

    private onRateChange(rateParam: number): void {
        const sortContext: ISortContext = this.context;
        sortContext.setGloveRating(this.props.glovesFilterSort, rateParam, !this.props.isMobile,
            !this.props.isMobile, !this.props.isMobile);
    }
}

